/*
 * Confetti burst used by the countdown reveal.
 *
 * Draws the Figma confetti shapes (see confetti-shapes.js) onto a throwaway
 * canvas with a simple paper-physics model: a fast launch out of the bottom
 * corners, air drag eating the upward speed, a terminal fall speed and a
 * sideways sway on the way down. Flakes fade out near the bottom edge and the
 * canvas removes itself once the last one is gone.
 */
import { CONFETTI_SHAPES, CONFETTI_COLORS } from "./confetti-shapes";

const GRAVITY = 1350; // px/s^2
const DRAG = 0.986; // share of velocity kept per 1/60s
const MAX_FALL = 300; // px/s, terminal velocity of a paper flake
const SWAY_ACC = 320; // px/s^2, sideways sway once falling
const SPIN_MIN = 3; // rad/s
const SPIN_MAX = 9;
const FADE_ZONE = 200; // px above the bottom edge where flakes fade out
const MAX_DT = 1 / 30; // clamp the step after a tab switch
const MAX_LIFETIME = 12000; // ms, hard stop so we never animate forever
const CANNONS = 4; // launch points spread along the bottom edge

const rand = (min, max) => min + Math.random() * (max - min);
const pickOne = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Path2D instances are shape-only, so they can be built once and shared.
let cachedPaths = null;

const getPaths = () => {
    if (!cachedPaths) {
        cachedPaths = CONFETTI_SHAPES.map((shape) => ({
            path: new Path2D(shape.d),
            w: shape.w,
            h: shape.h,
        }));
    }
    return cachedPaths;
};

const createParticle = (originX, originY, angleMin, angleMax, speedScale) => {
    const shape = pickOne(getPaths());
    const angle = rand(angleMin, angleMax);
    const speed = rand(2050, 2600) * speedScale;

    return {
        shape,
        x: originX + rand(-40, 40),
        y: originY + rand(-20, 20),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        scale: rand(0.75, 1.25),
        color: pickOne(CONFETTI_COLORS),
        spin: rand(0, Math.PI * 2),
        spinV: rand(SPIN_MIN, SPIN_MAX) * (Math.random() < 0.5 ? -1 : 1),
        flip: rand(0, Math.PI * 2),
        flipV: rand(2.5, 7) * (Math.random() < 0.5 ? -1 : 1),
        swayPhase: rand(0, Math.PI * 2),
        swayV: rand(1.6, 3.4),
        opacity: 1,
        // Flakes are launched from below the bottom edge, so fading and culling
        // stay switched off until they have actually made it onto the screen.
        entered: false,
    };
};

export function launchConfetti({ count } = {}) {
    if (typeof Path2D === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = document.createElement("canvas");
    canvas.className = "confetti-canvas";
    canvas.setAttribute("aria-hidden", "true");

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    document.body.appendChild(canvas);

    let width = 0;
    let height = 0;

    const resize = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        width = window.innerWidth;
        height = window.innerHeight;
        canvas.width = Math.floor(width * dpr);
        canvas.height = Math.floor(height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();

    const isNarrow = width < 768;
    const total = count || (isNarrow ? 405 : 600);
    const speedScale = isNarrow ? 0.82 : 1;
    // Every cannon leans towards the middle of the screen: full tilt at the two
    // corners, straight up in the middle. Narrow screens get a steeper launch
    // overall, otherwise the flatter shots leave the frame sideways before
    // anyone sees them. Both values are fractions of PI off vertical.
    const maxTilt = isNarrow ? 0.07 : 0.14;
    const halfSpread = isNarrow ? 0.05 : 0.1;
    const perCannon = Math.round(total / CANNONS);

    const particles = [];
    for (let cannon = 0; cannon < CANNONS; cannon += 1) {
        // 0 at the bottom-left corner, 1 at the bottom-right one. The outermost
        // pair sits just off-screen so their flakes fly in from beyond the edge.
        const position = cannon / (CANNONS - 1);
        const originX = -30 + position * (width + 60);
        const aim = -Math.PI * (0.5 - maxTilt * (1 - 2 * position));

        for (let i = 0; i < perCannon; i += 1) {
            particles.push(
                createParticle(
                    originX,
                    height + 30,
                    aim - Math.PI * halfSpread,
                    aim + Math.PI * halfSpread,
                    speedScale
                )
            );
        }
    }

    let frame = null;
    let lastTime = null;
    let elapsed = 0;
    let stopped = false;

    const stop = () => {
        if (stopped) return;
        stopped = true;
        if (frame !== null) cancelAnimationFrame(frame);
        window.removeEventListener("resize", resize);
        canvas.remove();
    };

    window.addEventListener("resize", resize);

    const step = (time) => {
        if (lastTime === null) lastTime = time;
        const dt = Math.min((time - lastTime) / 1000, MAX_DT);
        lastTime = time;
        elapsed += dt * 1000;

        ctx.clearRect(0, 0, width, height);

        // Drag is defined per 1/60s, so scale it to the actual frame length.
        const damping = Math.pow(DRAG, dt * 60);
        const fadeStart = height - FADE_ZONE;

        for (let i = particles.length - 1; i >= 0; i -= 1) {
            const p = particles[i];

            p.vy += GRAVITY * dt;
            p.vx *= damping;
            p.vy *= damping;

            // Sway only kicks in once the flake is actually falling, so it does
            // not disturb the launch arc.
            const falling = Math.min(1, Math.max(0, p.vy / 200));
            if (falling > 0) {
                p.swayPhase += p.swayV * dt;
                p.vx += Math.sin(p.swayPhase) * SWAY_ACC * falling * dt;
            }

            if (p.vy > MAX_FALL) p.vy = MAX_FALL;

            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.spin += p.spinV * dt;
            p.flip += p.flipV * dt;

            if (!p.entered && p.y < fadeStart) p.entered = true;

            p.opacity = p.entered && p.y > fadeStart
                ? Math.max(0, 1 - (p.y - fadeStart) / FADE_ZONE)
                : 1;

            const reach = Math.max(p.shape.w, p.shape.h) * p.scale;
            const belowScreen = p.vy > 0 && p.y - reach > height;
            const offSideways = p.x + reach < -200 || p.x - reach > width + 200;

            if (belowScreen || offSideways || (p.entered && p.opacity <= 0)) {
                particles.splice(i, 1);
                continue;
            }

            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.spin);
            // Squashing one axis fakes the flake tumbling through 3D space;
            // clamped so an edge-on flake stays a hairline instead of vanishing.
            ctx.scale(p.scale, p.scale * Math.max(0.08, Math.abs(Math.cos(p.flip))));
            ctx.globalAlpha = p.opacity;
            ctx.fillStyle = p.color;
            ctx.translate(-p.shape.w / 2, -p.shape.h / 2);
            ctx.fill(p.shape.path);
            ctx.restore();
        }

        if (!particles.length || elapsed > MAX_LIFETIME) {
            stop();
            return;
        }

        frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);

    return stop;
}
